import { Button, Modal, Skeleton, Spin } from 'antd';
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import { mdlAgencyDetailRequest } from '../../models/service-models/agency/AgencyDetailRequest';
import { AgencyService } from '../../services/AgencyService';
import { mdlAgencyUpdateRequest } from '../../models/service-models/agency/AgencyUpdateRequest';
import { toast } from 'react-toastify';
import { ObjectHelper } from '../../utils/ObjectHelper';
import { useTranslation } from 'react-i18next';
import { SFModal } from '../elements/SFModal';
import AgencyContact from './AgencyContact';
import fallbackImage from "../../assets/img/agency-no-logo.jpg";
import bannerImage from "../../assets/img/h2s_banner.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

export type AgencyListProps = {
    handleBackClick?: () => void;
    callback?: () => void;
    hotelId: string;
    id: string;
};


const AgencyDetail: React.FC<AgencyListProps> = ({ handleBackClick, hotelId, id, callback }) => {
    const { t } = useTranslation();
    const [isActive, setIsActive] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [openContactModal, setOpenContactModal] = useState<boolean>(false);

    const { data, isLoading, refetch, isError } = useQuery(
        ["getBoardDetail", id],
        () => {
            if (id) {
                const request: mdlAgencyDetailRequest = { id, hotelId };
                return AgencyService.Detail(request);
            }
        },
        {
            staleTime: 0,
            refetchOnWindowFocus: false,
            enabled: !!id,
        }
    );

    useEffect(() => {
        if (data?.body?.data) {
            setIsActive(data.body.data.isCross);
        }
    }, [data]);

    const { mutateAsync: saveAgency, isLoading: mutateLoading } = useMutation(
        (data: mdlAgencyUpdateRequest) => AgencyService.Update(data),
        {
            onError: (error) => {
                console.error(error);
                toast.error(t("common.errorSaving"));
            },
            onSuccess: (data) => {
                if (data.header?.success && data.body?.isSuccessful) {
                    toast.success(t("common.success"));
                    refetch();
                    callback?.();
                } else {
                    toast.error((data.header?.messages && data.header.messages?.length > 0) ? ObjectHelper.getErrorMessage(data.header?.messages) : data.body?.message);
                }
            },
        }
    );



    const handleDisconnect = async () => {
        await saveAgency({ active: false, hotelId, id });
    };
    const handleConnect = async () => {
        await saveAgency({ active: true, hotelId, id });
    };
    const showDisconnectModal = () => {
        setIsModalVisible(true);
    };

    const handleModalOk = () => {
        setIsModalVisible(false);
        handleDisconnect();
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };


    return (
        <Spin spinning={mutateLoading} className="mt-4">
            <div className="d-flex justify-content-end mb-3">
                <Button className='btn btn-primary' onClick={handleBackClick}><FontAwesomeIcon icon={faArrowLeft} /> {t("common.backToList")}</Button>
            </div>
            <hr />
            <div className='container'>
                {isLoading && (
                    <>
                        <Skeleton active />
                        <Skeleton active />
                        <Skeleton active />
                    </>
                )}
                {data?.body?.data && (
                    <div className="agency-detail d-flex">
                        <div className="info-section" style={{ flex: 1, marginRight: '20px' }}>
                            <h2>{data.body.data.agencyName}</h2>

                            <img
                                src={data.body.data.imageSrc || fallbackImage}
                                alt={data.body.data.agencyName}
                                className="card-img-top my-3"
                                style={{ width: '200px' }}
                                onError={(e) => {
                                    e.currentTarget.src = fallbackImage;
                                }}
                            />

                            <div className="mb-3 d-flex">
                                <p className={isActive ? "text-success" : "text-danger"}><strong> {isActive ? t("agencies.mapped") : t("agencies.notMapped")}</strong> </p>

                                {!isActive && <Button

                                    className={`btn btn-info ms-2`}
                                    onClick={() => setOpenContactModal(true)}
                                >
                                    {t("agencies.contact")}
                                </Button>}
                            </div>
                            <div className='info-area'>
                                <div className="mb-2">
                                    <p><strong>{t("agencies.foundedYear")}: </strong> {data.body.data.foundedYear}</p>
                                    <p><strong>{t("agencies.workMarkets")}: </strong> {data.body.data.workMarkets?.map(wm => wm.name).join(", ")}</p>
                                    <p><strong>{t("agencies.companyExecution")}: </strong> {data.body.data.companyExecution}</p>
                                    <p>
                                        <strong>{t("agencies.website")}: </strong>
                                        <a href={`http://${data.body.data.webSite}`} target="_blank" rel="noopener noreferrer">
                                            {data.body.data.webSite}
                                        </a>
                                    </p>
                                    <p><strong>{t("agencies.officeAddress")}:</strong> {data.body.data.officeAddress}</p>
                                    <p><strong>{t("agencies.phone")}:</strong> {data.body.data.phone}</p>
                                    <p><strong>{t("agencies.regionalOffices")}:</strong> {data.body.data.regionalOffices}</p>
                                </div>

                                <div className="mb-2">
                                    <p>
                                        <strong>{t("agencies.contractEmail")}: </strong>
                                        <a href={`mailto:${data.body.data.contractEmail}`}>
                                            {data.body.data.contractEmail}
                                        </a>
                                    </p>
                                    <p>
                                        <strong>{t("agencies.reservationEmail")}: </strong>
                                        <a href={`mailto:${data.body.data.reservationEmail}`}>
                                            {data.body.data.reservationEmail}
                                        </a>
                                    </p>
                                    <p>
                                        <strong>{t("agencies.operationEmail")}: </strong>
                                        <a href={`mailto:${data.body.data.operationEmail}`}>
                                            {data.body.data.operationEmail}
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="banner-section" style={{ flex: 1 }}>
                            <img
                                src={bannerImage}
                                alt="Agency Banner"
                                style={{ width: '90%', height: 'auto', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <Modal
                title={t("agencies.disconnectConfirmation")}
                visible={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Yes"
                cancelText="No"
            >
                <p>{t("agencies.alertDisconnect")}</p>
            </Modal>
            <SFModal
                width={window.innerWidth * 33 / 100}
                open={openContactModal}
                toggler={() => { setOpenContactModal(!openContactModal) }}
                body={<AgencyContact hotelId={hotelId} agencyId={id} agencyName={data?.body?.data.agencyName} callback={() => setOpenContactModal(false)} />}
                title={t("agencies.contact")}
            />
        </Spin>
    );
};

export default AgencyDetail;
